import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import React from 'react';
import { useInView } from 'react-intersection-observer';
import ProfilePageNavbar from '../../components/ProfilePageNavbar/ProfilePageNavbar';
import { Button, ShowIf } from '../../flayr-components';
import useDeviceMediaQuery from '../../hooks/useDeviceMediaQuery';
import { useScrollSpyContext } from '../../lib/ScrollSpy/ScrollSpypContext';
import smoothscroll from 'smoothscroll-polyfill';

smoothscroll.polyfill();
interface IProfileDisplayLayoutProps {
  navbarSettings?: {
    disableBackBtn?: boolean;
    onBack?: (e: any) => void;
    backBtnLabel?: string;
    cta?: React.ReactNode;
    onCtaClick?: (e: any) => void;
    ctaExtension?: (e: any) => React.ReactNode;
    message?: React.ReactNode;
    onLearnMore?: (e?: any) => void;
    onLinkClick?: (e: any) => void;
    profileImg?: string;
    name?: string;
    reviewScore?: number;
    reviewCount?: number;
    featured?: boolean;
    links?: string[];
    cancellationPolicyLabel?: string;
    hideCancellationPolicy?: boolean;
  };
  alertBar?: React.ReactNode;
  alertBarSticky?: boolean;
  mediaSection?: React.ReactNode;
  avatarSection?: React.ReactNode;
  quoteDetailsSection?: React.ReactNode;
  pricingInclusionsSection?: React.ReactNode;
  ctaSection?: React.ReactNode;
  freeCancellationSection?: React.ReactNode;
  testimonialsSection?: React.ReactNode;
  bioSection?: React.ReactNode;
  kitSection?: React.ReactNode;
  reviewsSection?: React.ReactNode;
  faqsSection?: React.ReactNode;
  helpSection?: React.ReactNode;
  footerSection?: React.ReactNode;
  modalsSection?: React.ReactNode;
  rescheduleSection?: React.ReactNode;
  changePackageSection?: React.ReactNode;
  reasonsToBookSection?: React.ReactNode;
  otherOffersSection?: React.ReactNode;
}

const ArrowSection: React.FC<React.PropsWithChildren<{
  target?: string;
  onClick?: (e?: any) => void;
  label?: string;
  paddingBottom?: boolean;
}>> = ({ target, onClick, label, paddingBottom = true }) => {
  return (
    <div
      className={classNames(
        'absolute bottom-0 left-0 w-full text-lg text-center',
        { 'pb-12': paddingBottom }
      )}
    >
      <button
        className="p-4 font-medium leading-tight"
        data-target={target}
        onClick={onClick}
      >
        <FontAwesomeIcon
          icon={faChevronDown}
          className="text-xl pointer-events-none"
        />
        <span className="block pointer-events-none">{label}</span>
      </button>
    </div>
  );
};

const ProfileDisplayLayout: React.FC<React.PropsWithChildren<IProfileDisplayLayoutProps>> = ({
  navbarSettings,
  alertBarSticky,
  alertBar: AlertBar,
  mediaSection: MediaSection,
  avatarSection: AvatarSection,
  quoteDetailsSection: QuoteDetailsSection,
  pricingInclusionsSection: PricingInclusionsSection,
  ctaSection: CtaSection,
  freeCancellationSection: FreeCancellationSection,
  testimonialsSection: TestimonialsSection,
  bioSection: BioSection,
  kitSection: KitSection,
  reviewsSection: ReviewsSection,
  faqsSection: FaqsSection,
  helpSection: HelpSection,
  footerSection: FooterSection,
  modalsSection: ModalsSection,
  rescheduleSection: RescheduleSection,
  reasonsToBookSection: ReasonsToBookSection,
  changePackageSection: ChangePackageSection,
  otherOffersSection: OtherOffersSection,
}) => {
  const { isDesktop } = useDeviceMediaQuery();
  const { setActiveSection } = useScrollSpyContext();
  const options = {
    threshold: 0.2,
  };
  const { ref: appointmentRef, inView: isAppointmentInView } =
    useInView(options);
  const { ref: pricingRef, inView: isPricingInView } = useInView(options);
  const { ref: bioRef, inView: isBioInView } = useInView(options);
  const { ref: ReviewsRef, inView: isReviewsInView } = useInView(options);
  const { ref: FAQsRef, inView: isFAQsInView } = useInView(options);

  React.useEffect(() => {
    let activeItem = '';
    if (isAppointmentInView) activeItem = 'appointment';
    if (isPricingInView) activeItem = 'pricing';
    if (isBioInView) activeItem = 'bio';
    if (isReviewsInView) activeItem = 'reviews';
    if (isFAQsInView) activeItem = 'FAQs';
    setActiveSection?.(activeItem);
  }, [
    isAppointmentInView,
    isPricingInView,
    isBioInView,
    isReviewsInView,
    isFAQsInView,
  ]);

  function scrollToSection(e) {
    const el = document.querySelector(`#${e.target.dataset.target}`);
    el?.scrollIntoView({
      behavior: 'smooth',
    });
  }

  return (
    <div className="w-full h-full overflow-y-auto lg:pb-10 scroll-smooth font-poppins tracking-[.05em] leading-[1.5em] text-sm text-gray-primary">
      <div
        data-testid="alert-bar"
        className={classNames('lg:mb-5 lg:hidden', {
          'sticky top-0 z-20 shadow lg:top-[55px]': alertBarSticky,
        })}
      >
        {AlertBar}
      </div>

      {/* used to be lg:w-10/12 lg:mx-auto */}
      <main className="w-full h-full px-0">
        {/* used to be md:gird instead of md:hidden */}
        <div className="w-full h-full gap-0 lg:gap-8 lg:hidden">
          {/* left section */}
          <section className="w-full h-full lg:col-span-8">
            {/* <div className="sticky top-0 md:hidden">{AlertBar}</div> */}
            {MediaSection}
            <div className="relative z-10 p-4 -mt-3 bg-white rounded-t-2xl">
              <section>{AvatarSection}</section>
              {/* visible on mobile hidden on desktop, but exists on right side */}
              <div className="mb-4 lg:hidden">{QuoteDetailsSection}</div>
              {/* visible on mobile hidden on desktop, but exists on right side */}
              {!!RescheduleSection && (
                <div className="mb-4 lg:hidden">{RescheduleSection}</div>
              )}
              {ChangePackageSection}
              {/* visible on mobile hidden on desktop, but exists on right side */}
              <section className="mb-5 lg:hidden">
                {PricingInclusionsSection}
              </section>
              {/* visible on mobile hidden on desktop, but exists on right side */}
              <div className="section lg:hidden">{FreeCancellationSection}</div>
              {!isDesktop && (
                <section className="lg:hidden">{CtaSection}</section>
              )}
              <section className="mt-12 text-gray-200 border-t divide-y divide-gray-200">
                {TestimonialsSection}
                {BioSection}
                {KitSection}
                {ReviewsSection}
                {FaqsSection}
                {!!OtherOffersSection && (
                  <div className="-mx-4">{OtherOffersSection}</div>
                )}
                {HelpSection}
              </section>
              <div className="mt-6">{ReasonsToBookSection}</div>
              {/* to balance out footer bar height */}
              <div className="h-32" />
            </div>
          </section>
        </div>

        {/* desktop view */}
        <section className="hidden w-full lg:block">
          {!navbarSettings?.disableBackBtn && (
            <ProfilePageNavbar
              {...navbarSettings}
              cta={(e) => {
                return (
                  <div className="flex">
                    {navbarSettings?.ctaExtension?.(e)}
                    <ShowIf
                      condition={typeof navbarSettings?.cta === 'string'}
                      elseTemplate={navbarSettings?.cta}
                    >
                      <Button
                        color="dark"
                        onClick={navbarSettings?.onCtaClick}
                        className="rounded-full"
                      >
                        {navbarSettings?.cta}
                      </Button>
                    </ShowIf>
                  </div>
                );
              }}
            />
          )}
          {AlertBar}

          {isDesktop && (
            <div className="hidden lg:flex">
              {/* image carousel */}
              <div className="bg-gray-200 h-screen w-[100vh] sticky top-0">
                {MediaSection}
              </div>
              <div className="flex-none w-[calc(100vw-100vh)] overflow-x-hidden">
                {!!QuoteDetailsSection && (
                  <section
                    className="relative flex items-center min-h-[calc(100vh-3.5rem)] p-12  pb-24"
                    id="appointment"
                  >
                    <div className="w-full" ref={appointmentRef}>
                      <div className="mb-12">
                        {QuoteDetailsSection}

                        {!!RescheduleSection && (
                          <div className="pt-8 mt-12">{RescheduleSection}</div>
                        )}
                        {!!ChangePackageSection && (
                          <div className="pt-8 mt-12">
                            {ChangePackageSection}
                          </div>
                        )}
                      </div>
                      <ArrowSection
                        onClick={scrollToSection}
                        target="pricing"
                        label="Pricing"
                      />
                    </div>
                  </section>
                )}
                {!!PricingInclusionsSection && (
                  <section
                    className="relative flex items-center min-h-screen p-12 pb-24 text-white bg-black"
                    id="pricing"
                    ref={pricingRef}
                  >
                    <div className="w-full">
                      <div className="mb-12">{PricingInclusionsSection}</div>
                      <div className="mb-12">{CtaSection}</div>

                      <ArrowSection
                        onClick={scrollToSection}
                        target="bio"
                        label="Bio"
                      />
                    </div>
                  </section>
                )}

                <section className="w-full">
                  <div className="w-full space-y-16">
                    <div
                      id="bio"
                      ref={bioRef}
                      className="relative min-h-screen px-12 pt-12 pb-24 space-y-12"
                    >
                      <div>
                        {AvatarSection}
                        {BioSection}
                      </div>
                      {KitSection}
                      {TestimonialsSection}
                      {(!!ReviewsSection && (
                        <ArrowSection
                          onClick={scrollToSection}
                          target="reviews"
                          label="Reviews"
                          paddingBottom={false}
                        />
                      )) || (
                        <ArrowSection
                          onClick={scrollToSection}
                          target="FAQs"
                          label="FAQs"
                          paddingBottom={false}
                        />
                      )}
                    </div>
                    {!!ReviewsSection && (
                      <div
                        id="reviews"
                        className="relative flex items-center min-h-screen px-12 pt-12 pb-24"
                        ref={ReviewsRef}
                      >
                        {ReviewsSection}

                        <ArrowSection
                          onClick={scrollToSection}
                          target="FAQs"
                          label="FAQs"
                          paddingBottom={false}
                        />
                      </div>
                    )}

                    <div
                      className="flex items-center min-h-screen px-12 pt-12"
                      id="FAQs"
                      ref={FAQsRef}
                    >
                      {FaqsSection}
                    </div>

                    <div>{OtherOffersSection}</div>

                    <div className="px-12">
                      <div className="mb-10 overflow-hidden rounded-2xl">
                        {HelpSection}
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          )}
        </section>
        {FooterSection}
        {ModalsSection}
      </main>
    </div>
  );
};
export default ProfileDisplayLayout;
